import { useEffect, useRef, useState } from 'react';
import {
  DevicePairingService,
  PairingSessionStatus,
} from '@/api/user-portal-api-generated';
import type {
  PairingData,
  UseDevicePairingParams,
  UseDevicePairingReturn,
} from '@/components/device-pairing/device-pairing.types';
import { DevicePairingStatus } from '@/enums/device-pairing-status.enums';
import {
  getOnboardingDevicePairingStatus,
  startOnboardingDevicePairing,
} from '@/features/onboarding/api/user-onboarding-api';
import { useInvalidateDevices } from '@/hooks/use-device-mutations.queries';

const POLLING_INTERVAL_MS = 2000;
// Tolerate client clock behind server and Redis TTL lag.
const EXPIRY_GRACE_MS = 30_000;

export function useDevicePairing({
  isOpen,
  onClose,
  isOnboarding,
  showIntro,
}: UseDevicePairingParams): UseDevicePairingReturn {
  const { invalidateDevices } = useInvalidateDevices();
  const [pairingData, setPairingData] = useState<PairingData | null>(null);
  // Start with INTRO status if showIntro is true, otherwise IDLE
  const [status, setStatus] = useState<DevicePairingStatus>(
    showIntro ? DevicePairingStatus.INTRO : DevicePairingStatus.IDLE
  );
  const [error, setError] = useState<string | null>(null);
  const [isPaired, setIsPaired] = useState(false);
  const hasStartedRef = useRef(false);
  const pairingAttemptRef = useRef(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Start pairing when dialog opens (skip if showing intro first)
  useEffect(() => {
    if (
      isOpen &&
      !hasStartedRef.current &&
      status !== DevicePairingStatus.INTRO
    ) {
      hasStartedRef.current = true;
      setStatus(DevicePairingStatus.STARTING);
      setError(null);
      const attempt = pairingAttemptRef.current;

      const startPairing = async (): Promise<void> => {
        try {
          // Use onboarding endpoint (session cookie) or enrollment endpoint (authenticated user session)
          const response = isOnboarding
            ? await startOnboardingDevicePairing()
            : await DevicePairingService.startDevicePairing();

          if (attempt !== pairingAttemptRef.current) {
            return;
          }

          setPairingData({
            pairingCode: response.pairing_code,
            expiresAt: new Date(response.expires_at),
          });
          setStatus(DevicePairingStatus.ACTIVE);
        } catch (err) {
          if (attempt !== pairingAttemptRef.current) {
            return;
          }

          setStatus(DevicePairingStatus.ERROR);
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to start device pairing'
          );
        }
      };

      void startPairing();
    }
  }, [isOpen, isOnboarding, status]);

  // Start polling for pairing completion when we have pairing data
  // Use dedicated lightweight status endpoint (like enrollment flow)
  useEffect(() => {
    if (pairingData && status === DevicePairingStatus.ACTIVE && !isPaired) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(async () => {
        if (isPaired) {
          return;
        }

        try {
          const statusResponse = isOnboarding
            ? await getOnboardingDevicePairingStatus(pairingData.pairingCode)
            : await DevicePairingService.getPairingStatus(
                pairingData.pairingCode
              );

          if (statusResponse.status === PairingSessionStatus.COMPLETED) {
            setIsPaired(true);
            setStatus(DevicePairingStatus.SUCCESS);
            void invalidateDevices();

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }

            // Close dialog after brief success display
            // Parent wizard will handle completion (calls /onboarding/complete)
            // Store timeout ref so it can be cleared if dialog closes manually
            autoCloseTimeoutRef.current = setTimeout(() => {
              autoCloseTimeoutRef.current = null;
              onClose();
            }, 500); // Small delay to show success state briefly
          } else if (
            statusResponse.status === PairingSessionStatus.EXPIRED ||
            statusResponse.status === PairingSessionStatus.CANCELLED
          ) {
            setStatus(DevicePairingStatus.EXPIRED);

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
          // PENDING - continue polling
        } catch {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // Server deletes the code at expiry, so polls then fail with 404.
          if (Date.now() >= pairingData.expiresAt.getTime() - EXPIRY_GRACE_MS) {
            setStatus(DevicePairingStatus.EXPIRED);

            return;
          }

          onClose();
        }
      }, POLLING_INTERVAL_MS);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [pairingData, status, isPaired, onClose, isOnboarding, invalidateDevices]);

  const [wasOpen, setWasOpen] = useState(isOpen);

  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);

    if (!isOpen) {
      setIsPaired(false);
      setPairingData(null);
      setStatus(
        showIntro ? DevicePairingStatus.INTRO : DevicePairingStatus.IDLE
      );
      setError(null);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      hasStartedRef.current = false;
      pairingAttemptRef.current += 1;

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Clear auto-close timeout to prevent double onClose calls
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  // Note: Dialog auto-closes after pairing succeeds (with small delay to show success)
  // FactorSteps will automatically show alternatives screen when hasAlternative=true

  const handleRetry = (): void => {
    hasStartedRef.current = false;
    setStatus(DevicePairingStatus.IDLE);
    setError(null);
    setPairingData(null);
    setIsPaired(false);

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Handle "Set up Core Mobile App" from intro screen
  const handleSetupApp = (): void => {
    setStatus(DevicePairingStatus.IDLE);
  };

  return { status, error, pairingData, handleRetry, handleSetupApp };
}
